import React from 'react';
import { animated, useTransition } from '@react-spring/web';

import { useAnimatedRouteState } from '@wildidea/components/AnimatedRoutes/AnimatedRoutes';
import { useThemeAnimations, useThemeBreakpoints } from '@wildidea/components/Theme/Theme';

import { LionBulb } from '@components/LionBulb.js';
import { PhoneFrame } from '@components/PhoneFrame.js';
import { Idea, Wild, WildIdea } from '@components/WildIdeaText.js';

import commutablyScreenshot from './img/commutably-mobile.png';
import onemapScreenshot from './img/onemap-mobile.png';
import rescueAllyScreenshot from './img/rescueally-mobile.png';
import snapgeoScreenshot from './img/snapgeo-mobile.png';
import watchablesWatch from './img/watchables-watch.png';

import * as css from './HomeRoute.module.css';

const HomeRoute: React.FC<{}> = () => {
  const breakpoints = useThemeBreakpoints();

  const { phase, onTransitionEnd } = useAnimatedRouteState();
  const active = phase === 'active';
  const { inPlace } = useThemeAnimations('Common');
  const splashTransition = useTransition(active, {
    ...inPlace,
    onRest: onTransitionEnd
  });
  const contentHeadingTransition = useTransition(active, inPlace);
  const contentLeftTransition = useTransition(active, inPlace);
  const contentRightTransition = useTransition(active && breakpoints.md, inPlace);

  return (
    <div className={css.layout}>
      {splashTransition((style, active) =>
        active ? (
          <animated.div style={style}>
            <div className={css.hook}>
              <h1 className={css.tagLine}>Inspiration can't be domesticated.</h1>
              {breakpoints.md ? (
                <WildIdea className={css.wildIdeaText} />
              ) : (
                <div className={css.wildIdeaText}>
                  <Wild />
                  <Idea />
                </div>
              )}
            </div>
            <div className={css.lionBulb}>
              <LionBulb aria-label="The Wild Idea logo: A stylized lion's head sprouting from the threads of a lightbulb" />
            </div>
          </animated.div>
        ) : null
      )}

      <div className={css.content}>
        {contentHeadingTransition((style, active) =>
          active ? (
            <animated.div style={style}>
              <h2>Apps</h2>
            </animated.div>
          ) : null
        )}

        <div className={css.contentRow}>
          {contentLeftTransition((style, active) =>
            active ? (
              <animated.div style={style}>
                <div className={css.appCard}>
                  <h3>Rescue Ally</h3>
                  <p>
                    <WildIdea style={{ display: 'inline', height: '1em', marginRight: '0.4em' }} />
                    is the developer of{' '}
                    <strong>
                      <a href="https://rescueally.org/">Rescue Ally</a>
                    </strong>
                    , in partnership with{' '}
                    <a href="https://backcountrypulse.org/">Backcountry Pulse</a>. It is the offline
                    first field medical app for wilderness first responders: a guided patient
                    assessment from the first life threats to the SOAP note, with nothing forgotten
                    along the way, and no signal needed.
                  </p>
                  <p>
                    Vitals come straight from a Bluetooth heart rate monitor, and Scribe listens to
                    your spoken assessment and fills in the note for you, right on the phone. Hand
                    the patient off with a PDF, a QR code or a satellite message, and keep the
                    published field guide a tap away. Teams get shared records and account
                    management. Available on iOS and Android, with a free trial.
                  </p>
                  {!breakpoints.md ? (
                    <div className={css.mobileScreenshot}>
                      <img
                        src={rescueAllyScreenshot}
                        className={css.screenshot}
                        style={{ objectPosition: 'center 13%' }}
                      />
                    </div>
                  ) : null}
                </div>

                <div className={css.appCard}>
                  <h3>
                    <a href="https://watchables.fit/" target="_blank">
                      Watchables
                    </a>
                  </h3>
                  <p>
                    <strong>
                      <a href="https://watchables.fit">Watchables</a>
                    </strong>{' '}
                    is an active pet, not an idle pet! A project conceived and built in partnership
                    with an 8 year old boy, Watchables are virtual pets that live on your smartwatch
                    and grow when you move. Steps, active minutes, or calories hatch new critters
                    and unlock play modes and additional pets.
                  </p>
                  {!breakpoints.md ? (
                    <div className={css.mobileScreenshot}>
                      <img
                        src={watchablesWatch}
                        className={css.screenshot}
                        style={{ objectFit: 'contain' }}
                      />
                    </div>
                  ) : null}
                  <p>
                    No ads, no tracking, no in-app purchases, and no penalty for a day off. It runs
                    on <strong>Apple Watch, Wear OS and Garmin!</strong>
                  </p>
                </div>

                <div className={css.appCard}>
                  <h3>
                    <a href="https://snapgeo.io/demo" target="_blank" rel="nofollow">
                      SnapGeo
                    </a>
                  </h3>
                  <p>
                    <strong>
                      <a href="https://snapgeo.io/create" target="_blank" rel="nofollow">
                        SnapGeo
                      </a>
                    </strong>{' '}
                    is a map explorer for your adventure photos. Point it at a Google Photos album
                    and a GPX, and it lays every photo along the track on a 3D map - with a timeline
                    you can scrub from the trailhead to camp and one link to share.
                  </p>
                  {!breakpoints.md ? (
                    <div className={css.mobileScreenshot}>
                      <img
                        src={snapgeoScreenshot}
                        className={css.screenshot}
                        style={{ objectPosition: 'center 30%' }}
                      />
                    </div>
                  ) : null}
                  <p>Make your own album today!</p>
                </div>

                <div className={css.appCard}>
                  <h3>
                    <a href="https://dev.onemap.earth/user/hiebj" target="_blank" rel="nofollow">
                      OneMap
                    </a>
                  </h3>
                  <p>
                    <strong>OneMap</strong> is a real-time satellite tracking and operations
                    dashboard for field teams, adventurers, enthusiasts, and outdoor organizations.
                    We render{' '}
                    <strong>
                      3D topographic and satellite maps overlaid with satellite tracking and
                      messages
                    </strong>{' '}
                    for Garmin devices, SPOT Personal Tracker and other realtime satellite trackers.
                  </p>
                  {!breakpoints.md ? (
                    <div className={css.mobileScreenshot}>
                      <img
                        src={onemapScreenshot}
                        className={css.screenshot}
                        style={{ objectPosition: 'center 20%' }}
                      />
                    </div>
                  ) : null}
                  <p>
                    In addition to detailed history for both individuals and organizations,{' '}
                    <strong>OneMap</strong> can follow multiple users at once in real time for field
                    operations. It can also display any currently-active trackers within an
                    organization.
                  </p>
                </div>

                <div className={css.appCard}>
                  <h3>
                    <a href="https://commutably.net" target="_blank" rel="nofollow">
                      Commutably
                    </a>
                  </h3>
                  <p>
                    When searching for a new residence, one of the most important factors is{' '}
                    <strong>drivability</strong>: how easy it is to get to the places you go most
                    often. It is also one of the hardest to determine.
                  </p>
                  {!breakpoints.md ? (
                    <div className={css.mobileScreenshot}>
                      <img src={commutablyScreenshot} className={css.screenshot} />
                    </div>
                  ) : null}

                  <p>
                    To solve this problem,
                    <WildIdea style={{ display: 'inline', height: '1em', margin: '0 0.4em' }} />
                    is working together with realtors in the Boulder area and beyond to develop{' '}
                    <strong>Commutably</strong>, the app that helps you estimate, visualize, and
                    compare your weekly driving time from different prospective homes or
                    neighborhoods.
                  </p>
                </div>
              </animated.div>
            ) : null
          )}

          {contentRightTransition((style, active) =>
            active ? (
              <animated.div style={style}>
                <div className={css.phoneDemoLayout}>
                  <div className={css.phoneDemoBlock}>
                    <PhoneFrame
                      className={[css.phone, css.rescueAllyScreenshot].filter(Boolean).join(' ')}
                    >
                      <img src={rescueAllyScreenshot} className={css.screenshot} />
                    </PhoneFrame>
                    <div className={css.phoneDemoCaption}>
                      <a href="https://rescueally.org/" target="_blank" rel="nofollow">
                        Rescue Ally
                      </a>
                    </div>
                  </div>
                  <div className={css.phoneDemoBlock}>
                    <img src={watchablesWatch} className={css.watchDemo} />
                    <div className={css.phoneDemoCaption}>
                      <a href="https://watchables.fit/" target="_blank">
                        Watchables
                      </a>
                    </div>
                  </div>
                  <div className={css.phoneDemoBlock}>
                    <PhoneFrame className={css.phone}>
                      <img src={snapgeoScreenshot} className={css.screenshot} />
                    </PhoneFrame>
                    <div className={css.phoneDemoCaption}>
                      <a href="https://snapgeo.io/demo" target="_blank" rel="nofollow">
                        SnapGeo
                      </a>
                    </div>
                  </div>
                  <div className={css.phoneDemoBlock}>
                    <PhoneFrame className={css.phone}>
                      <img src={onemapScreenshot} className={css.screenshot} />
                    </PhoneFrame>
                    <div className={css.phoneDemoCaption}>
                      <a href="https://dev.onemap.earth/user/hiebj" target="_blank" rel="nofollow">
                        OneMap
                      </a>
                    </div>
                  </div>
                  <div className={css.phoneDemoBlock}>
                    <PhoneFrame className={css.phone}>
                      <img src={commutablyScreenshot} className={css.screenshot} />
                    </PhoneFrame>
                    <div className={css.phoneDemoCaption}>
                      <a href="https://commutably.net" target="_blank" rel="nofollow">
                        Commutably
                      </a>
                    </div>
                  </div>
                </div>
              </animated.div>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeRoute;
